/** @jsx jsx */
/** @jsxFrag */
import { useState, useCallback, useMemo, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled, css, jsx } from '@compiled/react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import OldButton from '@atlaskit/button/custom-theme-button';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Flex } from '@atlaskit/primitives/compiled';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import AppIcon from '@atlaskit/icon/core/app';
import AppsIcon from '@atlaskit/icon/core/apps';
import { fg as platform_fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import Tooltip from '@atlaskit/tooltip';
import Image from '@atlaskit/image';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	ToolbarTooltip,
	ToolbarDropdownItemSection,
	ToolbarDropdownItem,
	ToolbarDropdownMenu,
	AppIcon as AppIconNew,
} from '@atlaskit/editor-toolbar';

import { VIEW_PAGE_CONTEXT_MENU_FORGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	ForgeKeyboardShortcut,
	ForgeKeyboardShortcutVisualizer,
} from '@confluence/forge-ui/entry-points/ForgeKeyboardShortcut.ts';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle.ts';
import type { ForgeUIExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import {
	openContextMenuClick,
	simulateItemClick,
	simulateItemMouseOver,
} from '@confluence/legacy-keyboard-shortcut-helpers/entry-points/legacyKeyboardShortcutHelpers.ts';
import { PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

const i18n = defineMessages({
	selectApp: {
		id: 'highlight-actions.select.app',
		defaultMessage: 'Select app',
		description: 'Tooltip label for the dropdown menu to show other apps',
	},
	apps: {
		id: 'highlight-actions.dropdown-group.apps',
		defaultMessage: 'Apps',
		description: 'Label for the dropdown group "Apps"',
	},
});

const oldDropdownTriggerWrapperStyle = css({
	display: 'flex',
	alignItems: 'center',
	padding: token('space.025'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > button, & > button:hover': {
		height: '100%',
		alignItems: 'center',
	},
});

const dropdownTriggerWrapperStyle = css({
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.050'),
	paddingBottom: token('space.050'),
	paddingRight: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupPaddingContainer = styled.div({
	padding: `${token('space.200')} ${token('space.300')}`,
});

const dropdownComponentListStyle = css({
	display: 'flex',
	flexDirection: 'column',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	// The Inline dialog has build-in padding, next margin will void it
	margin: `${token('space.negative.200')} ${token('space.negative.300')}`,
	overflowY: 'auto',
	maxHeight: '300px',
	maxWidth: '300px',
});

const iconImageStyle = css({
	height: '24px',
	width: '24px',
});

const noIconWrapperStyle = css({
	width: '24px',
	height: '24px',
	borderRadius: token('radius.full'),
	backgroundColor: token('color.background.disabled'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-around',
});

type OtherActionsProps = {
	builtInActions?: React.JSX.Element[];
	connectItems: any[];
	forgeItems?: ReadonlyArray<ForgeUIExtensionType>;
	selectForgeExtension?: (extension: ForgeUIExtensionType) => void;
};

export const OtherActions = ({
	builtInActions = [],
	connectItems,
	forgeItems = [],
	selectForgeExtension = () => {},
}: OtherActionsProps) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isNewToolbarEnabled =
		fg('aifc_create_enabled') ||
		expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true);

	const fireOverflowMenuAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				name: 'button clicked - highlightActionsMenuOverflow',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'highlightActionsMenuOverflow',
				source: 'highlightActionsMenu',
				type: 'ui',
				attributes: {
					pageMode: PageMode.VIEW,
				},
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const onClose = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	const connectComponents = useMemo(
		() =>
			connectItems.map((item) => {
				if (expValEquals('platform_editor_renderer_toolbar_updates', 'isEnabled', true)) {
					return (
						<ToolbarDropdownItem
							key={item.completeKey}
							data-vc="link-item"
							data-key={item.completeKey}
							href={item.url}
							title={item.label}
							shouldTitleWrap={false}
							elemBefore={
								item?.icon?.path ? (
									<Image css={iconImageStyle} src={item.icon.path} alt={item.label} />
								) : (
									<AppIconNew size="small" label="" />
								)
							}
						>
							{item.label}
						</ToolbarDropdownItem>
					);
				}

				return editorExperiment('platform_editor_controls', 'variant1') ? (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					<div className={item.styleClass}>
						<DropdownItem
							key={item.completeKey}
							data-key={item.completeKey}
							href={item.url}
							title={item.label}
							shouldTitleWrap={false}
							elemBefore={
								item?.icon?.path ? (
									// eslint-disable-next-line @atlassian/a11y/alt-text -- See https://go/a11y-alt-text for more details
									<Image css={iconImageStyle} src={item.icon.path} />
								) : (
									<AppIcon label="" />
								)
							}
						>
							{item.label}
						</DropdownItem>
					</div>
				) : (
					<OldButton
						iconBefore={
							item?.icon?.path ? (
								// eslint-disable-next-line @atlassian/a11y/alt-text -- See https://go/a11y-alt-text for more details
								<Image css={iconImageStyle} src={item.icon.path} />
							) : (
								<div css={noIconWrapperStyle}>
									<AppIcon label="No icon" color={token('color.icon.disabled')} />
								</div>
							)
						}
						key={item.completeKey}
						appearance="subtle"
						data-key={item.completeKey}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={item.styleClass}
						href={item.url}
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							textAlign: 'left', // we can't wrap Button with `styled` as inner styles override this
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							borderRadius: 0,
						}}
						shouldFitContainer
						title={item.label}
					>
						{item.label}
					</OldButton>
				);
			}),
		[connectItems],
	);

	const forgeComponents = useMemo(
		() =>
			forgeItems.map((item) => {
				const appTitle = extensionTitle(item);
				if (expValEquals('platform_editor_renderer_toolbar_updates', 'isEnabled', true)) {
					return (
						<ForgeKeyboardShortcutVisualizer key={item.id} module={item}>
							<ToolbarDropdownItem
								key={item.id}
								title={appTitle}
								shouldTitleWrap={false}
								elemBefore={<AppIconNew size="small" label="" />}
								onClick={() => {
									experienceTracker.start({
										name: VIEW_PAGE_CONTEXT_MENU_FORGE_EXPERIENCE,
										id: item.id,
										timeout: 10000,
									});
									selectForgeExtension(item);
								}}
							>
								{appTitle}
							</ToolbarDropdownItem>
						</ForgeKeyboardShortcutVisualizer>
					);
				}

				return editorExperiment('platform_editor_controls', 'variant1') ? (
					<ForgeKeyboardShortcutVisualizer key={item.id} module={item}>
						<DropdownItem
							key={item.id}
							title={appTitle}
							shouldTitleWrap={false}
							elemBefore={<AppIcon label="" />}
							onClick={() => {
								experienceTracker.start({
									name: VIEW_PAGE_CONTEXT_MENU_FORGE_EXPERIENCE,
									id: item.id,
									timeout: 10000,
								});
								selectForgeExtension(item);
							}}
						>
							{appTitle}
						</DropdownItem>
					</ForgeKeyboardShortcutVisualizer>
				) : (
					<ForgeKeyboardShortcutVisualizer key={item.id} module={item}>
						<OldButton
							key={item.id}
							id={item.id}
							appearance="subtle"
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								textAlign: 'left', // we can't wrap Button with `styled` as inner styles override this

								paddingLeft: connectItems.length ? '40px' : '8px',
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								borderRadius: 0,
							}}
							onClick={() => {
								experienceTracker.start({
									name: VIEW_PAGE_CONTEXT_MENU_FORGE_EXPERIENCE,
									id: item.id,
									timeout: 10000,
								});
								selectForgeExtension(item);
							}}
							shouldFitContainer
							title={appTitle}
						>
							{appTitle}
						</OldButton>
					</ForgeKeyboardShortcutVisualizer>
				);
			}),
		[connectItems, forgeItems, selectForgeExtension, experienceTracker],
	);

	const forgeKeyboardShortcuts = useMemo(
		() =>
			forgeItems.map((module) => (
				<ForgeKeyboardShortcut
					key={module.id}
					module={module}
					action={() => {
						openContextMenuClick();
						simulateItemClick(module.id);
						simulateItemMouseOver(module.id);
					}}
				/>
			)),
		[forgeItems],
	);

	const dropDownComponents = useMemo(() => {
		const oldToolbarComponents = editorExperiment('platform_editor_controls', 'variant1') ? (
			<div data-testid="context-menu-dropdown-groups">
				{builtInActions.length > 0 && (
					<DropdownItemGroup testId="context-menu-dropdown-group-builtin">
						{builtInActions}
					</DropdownItemGroup>
				)}
				{Boolean(connectItems.length || forgeItems.length) && (
					<DropdownItemGroup
						hasSeparator={builtInActions.length > 0}
						title={intl.formatMessage(i18n.apps)}
						testId="context-menu-dropdown-group-apps"
					>
						{connectComponents}
						{forgeComponents}
					</DropdownItemGroup>
				)}
			</div>
		) : (
			<div css={dropdownComponentListStyle}>
				{connectComponents}
				{forgeComponents}
			</div>
		);

		return isNewToolbarEnabled ? (
			<div data-testid="context-menu-dropdown-groups">
				{builtInActions.length > 0 && (
					<ToolbarDropdownItemSection testId="context-menu-dropdown-group-builtin">
						{builtInActions}
					</ToolbarDropdownItemSection>
				)}
				{Boolean(connectItems.length || forgeItems.length) && (
					<ToolbarDropdownItemSection
						hasSeparator={builtInActions.length > 0}
						title={intl.formatMessage(i18n.apps)}
						testId="context-menu-dropdown-group-apps"
					>
						{connectComponents}
						{forgeComponents}
					</ToolbarDropdownItemSection>
				)}
			</div>
		) : (
			oldToolbarComponents
		);
	}, [
		isNewToolbarEnabled,
		builtInActions,
		connectItems.length,
		forgeItems.length,
		intl,
		connectComponents,
		forgeComponents,
	]);

	if (isNewToolbarEnabled) {
		if (!connectItems.length && !forgeItems.length && !builtInActions.length) {
			return null;
		}

		if (expValEquals('platform_editor_renderer_toolbar_updates', 'isEnabled', true)) {
			if (expValEquals('platform_editor_hide_toolbar_tooltips_fix', 'isEnabled', true)) {
				return (
					<ToolbarDropdownMenu
						iconBefore={
							<ShowMoreHorizontalIcon label={intl.formatMessage(i18n.selectApp)} size="small" />
						}
						id="context-menu-other-apps"
						testId="context-menu-other-apps"
						onClick={fireOverflowMenuAnalytics}
						tooltipComponent={<ToolbarTooltip content={intl.formatMessage(i18n.selectApp)} />}
					>
						{dropDownComponents}
						{forgeKeyboardShortcuts}
					</ToolbarDropdownMenu>
				);
			}

			return (
				<ToolbarTooltip content={intl.formatMessage(i18n.selectApp)}>
					<ToolbarDropdownMenu
						iconBefore={
							<ShowMoreHorizontalIcon label={intl.formatMessage(i18n.selectApp)} size="small" />
						}
						id="context-menu-other-apps"
						testId="context-menu-other-apps"
						onClick={fireOverflowMenuAnalytics}
					>
						{dropDownComponents}
						{forgeKeyboardShortcuts}
					</ToolbarDropdownMenu>
				</ToolbarTooltip>
			);
		}

		return (
			<ToolbarTooltip content={intl.formatMessage(i18n.selectApp)}>
				<DropdownMenu
					shouldRenderToParent
					placement="bottom-start"
					isOpen={isOpen}
					onOpenChange={(attrs) => {
						setIsOpen(attrs.isOpen);
					}}
					trigger={({ triggerRef, ...triggerProps }) => (
						<div
							{...triggerProps}
							ref={triggerRef}
							css={dropdownTriggerWrapperStyle}
							id="context-menu-other-apps"
							data-testid="context-menu-other-apps"
						>
							<IconButton
								appearance="subtle"
								isSelected={isOpen}
								icon={ShowMoreHorizontalIcon}
								isTooltipDisabled={false}
								label={intl.formatMessage(i18n.selectApp)}
								testId="highlight-actions-show-more"
								onClick={fireOverflowMenuAnalytics}
							/>
							{forgeKeyboardShortcuts}
						</div>
					)}
				>
					{dropDownComponents}
				</DropdownMenu>
			</ToolbarTooltip>
		);
	}

	if (editorExperiment('platform_editor_controls', 'variant1')) {
		if (!connectItems.length && !forgeItems.length && !builtInActions.length) {
			return null;
		}
	} else {
		if (!connectItems.length && !forgeItems.length) {
			return null;
		}
	}

	return editorExperiment('platform_editor_controls', 'variant1') ? (
		<DropdownMenu
			shouldRenderToParent
			placement="bottom-start"
			isOpen={isOpen}
			onOpenChange={(attrs) => {
				setIsOpen(attrs.isOpen);
			}}
			trigger={(triggerProps) => (
				<div
					{...triggerProps}
					ref={triggerProps.triggerRef}
					css={dropdownTriggerWrapperStyle}
					id="context-menu-other-apps"
					data-testid="context-menu-other-apps"
				>
					<IconButton
						appearance="subtle"
						isSelected={isOpen}
						icon={ShowMoreHorizontalIcon}
						isTooltipDisabled={false}
						label={intl.formatMessage(i18n.selectApp)}
						testId="highlight-actions-show-more"
						onClick={fireOverflowMenuAnalytics}
					/>
					{forgeKeyboardShortcuts}
				</div>
			)}
		>
			{dropDownComponents}
		</DropdownMenu>
	) : (
		<Popup
			placement="bottom-start"
			isOpen={isOpen}
			onClose={onClose}
			autoFocus={false} // keep this param as FF unselect the text after the trigger button is pressed
			content={() => <PopupPaddingContainer>{dropDownComponents}</PopupPaddingContainer>}
			trigger={(triggerProps) => (
				<div
					{...triggerProps}
					css={[
						editorExperiment('platform_editor_controls', 'variant1')
							? oldDropdownTriggerWrapperStyle
							: undefined,
					]}
				>
					<Tooltip content={intl.formatMessage(i18n.selectApp)}>
						{(tooltipProps) => (
							<OldButton
								{...tooltipProps}
								id="context-menu-other-apps"
								aria-expanded={isOpen}
								appearance="subtle"
								isSelected={isOpen}
								onClick={() => setIsOpen(!isOpen)}
								iconBefore={
									editorExperiment('platform_editor_controls', 'variant1') ? (
										<ShowMoreHorizontalIcon
											label=""
											spacing="spacious"
											testId="highlight-actions-show-more"
										/>
									) : (
										<Flex alignItems="center" gap="space.050">
											<AppsIcon label="" />
											<ChevronDownIcon
												label="Drop down"
												testId="highlight-actions-show-more"
												size="small"
											/>
										</Flex>
									)
								}
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									margin: editorExperiment('platform_editor_controls', 'variant1')
										? undefined
										: token('space.025'),
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									marginLeft: token('space.negative.025'),
								}}
								testId="context-menu-other-apps"
							/>
						)}
					</Tooltip>
					{forgeKeyboardShortcuts}
				</div>
			)}
			shouldRenderToParent={platform_fg('should-render-to-parent-should-be-truecomments')}
		/>
	);
};
